// P1B-FE FE-2 — recent stream_sessions for a camera + expandable "what happened".
import { useState } from "react";

import {
  useSessionExplain,
  useStreamSessions,
  type StreamSessionRow,
} from "@/api/streamSessions";
import { Badge } from "@/components/ui/badge";

function statusVariant(status: string): "online" | "offline" | "warning" | "error" {
  if (status === "ACTIVE" || status === "ADMITTED") return "online";
  if (status === "FAILED") return "error";
  if (status === "DEGRADED") return "warning";
  return "offline";
}

function ExplainCard({ sessionId }: { sessionId: string }) {
  const { data, isLoading, isError } = useSessionExplain(sessionId);
  if (isLoading)
    return <p className="px-3 py-2 text-xs text-muted-foreground">Loading…</p>;
  if (isError || !data)
    return <p className="px-3 py-2 text-xs text-red-600">Could not load details.</p>;
  return (
    <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="session-explain">
      <p className="font-medium">{data.what_happened}</p>
      <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <dt>State</dt>
        <dd>{data.status_label}</dd>
        {data.selection_label && (
          <>
            <dt>Selection</dt>
            <dd>{data.selection_label}</dd>
          </>
        )}
        {data.failure_label && (
          <>
            <dt>Reason</dt>
            <dd className="text-red-600">{data.failure_label}</dd>
          </>
        )}
        {data.failure_detail && (
          <>
            <dt>Detail</dt>
            <dd className="font-mono">{data.failure_detail}</dd>
          </>
        )}
      </dl>
    </div>
  );
}

function SessionRow({ s }: { s: StreamSessionRow }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border-b last:border-b-0" data-testid="session-row">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-1 py-2 text-left text-sm hover:bg-muted"
      >
        <Badge variant={statusVariant(s.status)} className="text-[10px]">
          {s.status_label}
        </Badge>
        <span className="text-xs text-muted-foreground">{s.purpose}</span>
        {s.failure_label && (
          <span className="text-xs text-red-600">{s.failure_label}</span>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground">
          {s.requested_at ? new Date(s.requested_at).toLocaleString() : ""}
        </span>
        <span className="text-xs text-muted-foreground">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="pb-2">
          <ExplainCard sessionId={s.id} />
        </div>
      )}
    </li>
  );
}

export function CameraSessionsPanel({ cameraId }: { cameraId: string }) {
  const { data, isLoading, isError } = useStreamSessions(cameraId);
  if (isLoading)
    return (
      <p className="text-sm text-muted-foreground" data-testid="sessions-loading">
        Loading sessions…
      </p>
    );
  if (isError)
    return (
      <p className="text-sm text-red-600" data-testid="sessions-error">
        Failed to load sessions.
      </p>
    );
  if (!data || data.length === 0)
    return (
      <p className="text-sm text-muted-foreground" data-testid="sessions-empty">
        No stream sessions recorded yet.
      </p>
    );
  return (
    <ul className="divide-y" data-testid="sessions-list">
      {data.map((s) => (
        <SessionRow key={s.id} s={s} />
      ))}
    </ul>
  );
}

export default CameraSessionsPanel;
